import { Box, useTheme, useMediaQuery, Drawer, Typography } from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { useChatClient } from './useChatClient';
import { ChatHeader } from './ChatHeader';
import { ChatMessageList } from './ChatMessageList';
import { ChatMessageInput } from './ChatMessageInput';
import { ChatTypingIndicator } from './ChatTypingIndicator';
import { ChatConversationInfo } from './ChatConversationInfo';
import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

export const ChatMessageArea = ({ onBack }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { selectedConversation, messages, isLoading, typingUsers } = useChatClient();
  const [showInfo, setShowInfo] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);
  const messageInputRef = useRef(null);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer?.types?.includes('Files')) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;

    const files = e.dataTransfer?.files;
    if (files && files.length > 0 && messageInputRef.current) {
      messageInputRef.current.addFiles(files);
    }
  }, []);

  if (!selectedConversation) return null;

  const InfoPanel = (
    <ChatConversationInfo
      conversation={selectedConversation}
      onClose={() => setShowInfo(false)}
      isMobile={isMobile}
    />
  );

  return (
    <Box
      className="flex h-full relative"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drop Zone Overlay */}
      {isDragging && (
        <Box
          className="absolute inset-0 z-50 flex flex-col items-center justify-center"
          sx={{
            backgroundColor: `${colors.info}15`,
            border: `3px dashed ${colors.info}`,
            borderRadius: '16px',
            margin: '8px',
            backdropFilter: 'blur(4px)',
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 64, color: colors.info, mb: 2 }} />
          <Typography variant="h6" sx={{ color: colors.info, fontWeight: 600 }}>
            {t('Drop files here')}
          </Typography>
          <Typography variant="body2" sx={{ color: colors.text, opacity: 0.6, mt: 0.5 }}>
            {t('Release to upload')}
          </Typography>
        </Box>
      )}

      <Box className="flex flex-col flex-1 min-w-0">
        {/* Header */}
        <ChatHeader
          conversation={selectedConversation}
          onBack={onBack}
          onInfoClick={() => setShowInfo(!showInfo)}
        />

        {/* Messages */}
        <Box className="flex-1 overflow-hidden">
          <ChatMessageList messages={messages} isLoading={isLoading} />
        </Box>

        {/* Typing Indicator */}
        <ChatTypingIndicator typingUsers={typingUsers} />

        {/* Input */}
        <ChatMessageInput ref={messageInputRef} />
      </Box>

      {/* Info Panel - Drawer on mobile, side panel on desktop */}
      {isMobile ? (
        <Drawer
          anchor="right"
          open={showInfo}
          onClose={() => setShowInfo(false)}
          PaperProps={{
            sx: {
              width: '100%',
              maxWidth: 400,
              backgroundColor: colors.glass,
              backdropFilter: 'blur(10px)',
            },
          }}
        >
          {InfoPanel}
        </Drawer>
      ) : (
        showInfo && InfoPanel
      )}
    </Box>
  );
};
